import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Gift, Search, Plus, Minus, User, Phone, Star, Clock, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/vd09zrou_Logo%20-%20Final.png";

export const AdminLoyalty = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stampsToModify, setStampsToModify] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');

  const fetchCustomers = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const url = search 
        ? `${API}/admin/rewards?search=${encodeURIComponent(search)}`
        : `${API}/admin/rewards`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchTransactions = async (phoneNumber) => {
    try {
      const response = await fetch(`${API}/admin/rewards/${encodeURIComponent(phoneNumber)}/transactions`, { 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    await fetchTransactions(customer.phone_number);
  };

  const handleAddStamps = async () => {
    if (!selectedCustomer) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API}/rewards/stamp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          phone_number: selectedCustomer.phone_number, 
          stamps_to_add: stampsToModify 
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedCustomer(updated);
        toast.success(`Added ${stampsToModify} stamp${stampsToModify > 1 ? 's' : ''}`);
        fetchCustomers();
        fetchTransactions(selectedCustomer.phone_number);
      } else {
        toast.error('Failed to add stamps');
      }
    } catch (error) {
      console.error('Error adding stamps:', error);
      toast.error('Failed to add stamps');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveStamps = async () => {
    if (!selectedCustomer) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/rewards/remove-stamp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          phone_number: selectedCustomer.phone_number, 
          stamps_to_remove: stampsToModify 
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedCustomer(updated);
        toast.success(`Removed ${stampsToModify} stamp${stampsToModify > 1 ? 's' : ''}`);
        fetchCustomers();
        fetchTransactions(selectedCustomer.phone_number);
      } else {
        toast.error('Failed to remove stamps');
      }
    } catch (error) {
      console.error('Error removing stamps:', error);
      toast.error('Failed to remove stamps');
    } finally {
      setProcessing(false);
    }
  };

  const handleRedeemFreeDrink = async () => {
    if (!selectedCustomer || selectedCustomer.free_drinks_earned < 1) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/rewards/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone_number: selectedCustomer.phone_number })
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedCustomer(updated);
        toast.success('Free drink redeemed!');
        fetchCustomers();
        fetchTransactions(selectedCustomer.phone_number);
      } else {
        toast.error('Failed to redeem');
      }
    } catch (error) {
      console.error('Error redeeming:', error);
      toast.error('Failed to redeem');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    if (!newPhone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setProcessing(true);
    try {
      const response = await fetch(`${API}/rewards/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone_number: newPhone, name: newName || null })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Customer added!');
        setNewPhone('');
        setNewName('');
        setShowAddNew(false);
        fetchCustomers();
        handleSelectCustomer(data);
      } else {
        toast.error('Failed to add customer');
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error('Failed to add customer');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateName = async () => {
    if (!selectedCustomer || !editName.trim()) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/rewards/update-name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone_number: selectedCustomer.phone_number, name: editName })
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedCustomer(updated);
        setEditingName(false);
        toast.success('Name updated!');
        fetchCustomers();
      } else {
        toast.error('Failed to update name');
      }
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Failed to update name');
    } finally {
      setProcessing(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchCustomers(query);
    }, 300);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-loyalty">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-sip-purple transition-colors">
                <ArrowLeft size={24} />
              </Link>
              <Link to="/">
                <img src={LOGO_URL} alt="Sip Fantasia" className="h-10 w-auto" />
              </Link>
            </div>
            <h1 className="font-script text-xl md:text-2xl text-gray-900">Loyalty Dashboard</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-lg text-gray-900">Customers</h2>
                <button
                  onClick={() => setShowAddNew(!showAddNew)}
                  className="text-sip-purple hover:text-sip-purple/80 p-1"
                  data-testid="add-customer-btn"
                >
                  <Plus size={20} />
                </button>
              </div>

              {/* Add New Customer Form */}
              {showAddNew && (
                <form onSubmit={handleAddNewCustomer} className="mb-4 p-3 bg-gray-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-2">Add new customer</p>
                  <div className="space-y-2">
                    <Input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Customer name (optional)"
                      data-testid="new-name-input"
                    />
                    <div className="flex gap-2">
                      <Input
                        type="tel"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        placeholder="Phone number *"
                        className="flex-1"
                        data-testid="new-phone-input"
                      />
                      <button
                        type="submit"
                        disabled={processing}
                        className="px-3 py-2 bg-sip-purple text-white rounded-lg hover:bg-sip-purple/90 disabled:opacity-50"
                      >
                        {processing ? <Loader2 className="animate-spin" size={16} /> : 'Add'}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Search by name or phone..."
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {/* Customer List */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="animate-spin w-6 h-6 mx-auto text-sip-purple" />
                  </div>
                ) : customers.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No customers found</p>
                ) : (
                  customers.map((customer) => (
                    <button
                      key={customer.card_id}
                      onClick={() => handleSelectCustomer(customer)}
                      className={`w-full text-left p-3 rounded-xl transition-colors ${
                        selectedCustomer?.card_id === customer.card_id 
                          ? 'bg-sip-purple/10 border-2 border-sip-purple' 
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      data-testid={`customer-${customer.card_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${customer.name ? 'bg-sip-purple/20' : 'bg-sip-yellow/20'}`}>
                          {customer.name ? (
                            <User size={16} className="text-sip-purple" />
                          ) : (
                            <Phone size={16} className="text-sip-yellow" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {customer.name && (
                            <p className="font-medium text-gray-900 truncate">{customer.name}</p>
                          )}
                          <p className={`${customer.name ? 'text-sm text-gray-500' : 'font-medium text-gray-900'} truncate`}>
                            {customer.phone_number}
                          </p>
                          <p className="text-xs text-gray-400">
                            {customer.stamps}/10 stamps • {customer.free_drinks_earned} free
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <button
                onClick={fetchCustomers}
                className="w-full mt-4 flex items-center justify-center gap-2 text-gray-600 hover:text-sip-purple py-2"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {/* Customer Details */}
          <div className="lg:col-span-2">
            {selectedCustomer ? (
              <div className="space-y-6">
                {/* Punch Card */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex-1">
                      {/* Name Display/Edit */}
                      {editingName ? (
                        <div className="flex items-center gap-2 mb-1">
                          <Input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Customer name"
                            className="max-w-xs"
                            autoFocus
                            data-testid="edit-name-input"
                          />
                          <button
                            onClick={handleUpdateName}
                            disabled={processing}
                            className="px-3 py-1 bg-sip-purple text-white text-sm rounded-lg hover:bg-sip-purple/90 disabled:opacity-50"
                          >
                            {processing ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingName(false)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mb-1">
                          {selectedCustomer.name ? (
                            <h2 className="font-semibold text-xl text-gray-900">{selectedCustomer.name}</h2>
                          ) : (
                            <span className="text-gray-400 italic">No name set</span>
                          )}
                          <button
                            onClick={() => {
                              setEditName(selectedCustomer.name || '');
                              setEditingName(true);
                            }}
                            className="text-sip-purple hover:text-sip-purple/80 p-1"
                            title="Edit name"
                            data-testid="edit-name-btn"
                          >
                            <User size={16} />
                          </button>
                        </div>
                      )}
                      <p className="font-medium text-gray-700">{selectedCustomer.phone_number}</p>
                      <p className="text-sm text-gray-500">Member since {formatDate(selectedCustomer.created_at)}</p>
                    </div>
                    {selectedCustomer.free_drinks_earned > 0 && (
                      <div className="bg-sip-yellow text-gray-900 px-4 py-2 rounded-full font-semibold">
                        {selectedCustomer.free_drinks_earned} FREE drink{selectedCustomer.free_drinks_earned > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {/* Visual Punch Card */}
                  <div className="bg-gradient-to-br from-sip-purple/10 to-sip-pink/10 rounded-xl p-6 mb-6">
                    <p className="text-center text-gray-600 mb-4">{selectedCustomer.stamps}/10 stamps collected</p>
                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 justify-items-center">
                      {[...Array(10)].map((_, i) => (
                        <div
                          key={`stamp-${i}`}
                          className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                            i < selectedCustomer.stamps 
                              ? 'bg-sip-purple border-sip-purple' 
                              : 'bg-white border-gray-300'
                          }`}
                        >
                          {i < selectedCustomer.stamps && (
                            <Star className="w-5 h-5 text-white fill-white" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stamp Controls */}
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Stamps:</span>
                      <select
                        value={stampsToModify}
                        onChange={(e) => setStampsToModify(Number(e.target.value))}
                        className="border rounded-lg px-3 py-2"
                        data-testid="stamps-select"
                      >
                        {[1, 2, 3, 4, 5].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={handleAddStamps}
                      disabled={processing}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      data-testid="add-stamps-btn"
                    >
                      {processing ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                      Add Stamps
                    </button>
                    <button
                      onClick={handleRemoveStamps}
                      disabled={processing || selectedCustomer.stamps === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      data-testid="remove-stamps-btn"
                    >
                      {processing ? <Loader2 className="animate-spin" size={16} /> : <Minus size={16} />}
                      Remove Stamps
                    </button>
                    {selectedCustomer.free_drinks_earned > 0 && (
                      <button
                        onClick={handleRedeemFreeDrink}
                        disabled={processing}
                        className="flex items-center gap-2 px-4 py-2 bg-sip-yellow text-gray-900 rounded-lg hover:bg-sip-yellow/90 disabled:opacity-50 font-semibold"
                        data-testid="redeem-btn"
                      >
                        {processing ? <Loader2 className="animate-spin" size={16} /> : <Gift size={16} />}
                        Redeem Free Drink
                      </button>
                    )}
                  </div>
                </div>

                {/* Transaction History */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="font-semibold text-lg text-gray-900 mb-4">Transaction History</h3>
                  {transactions.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No transactions yet</p>
                  ) : (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {transactions.map((tx, index) => (
                        <div
                          key={tx.timestamp || `tx-${index}`}
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            tx.type === 'add' ? 'bg-green-50' : 
                            tx.type === 'remove' ? 'bg-red-50' : 
                            tx.type === 'redeem' ? 'bg-yellow-50' : 'bg-gray-50'
                          }`}
                        >
                          <div className={`p-2 rounded-full ${
                            tx.type === 'add' ? 'bg-green-100' : 
                            tx.type === 'remove' ? 'bg-red-100' : 
                            tx.type === 'redeem' ? 'bg-yellow-100' : 'bg-gray-100'
                          }`}>
                            {tx.type === 'add' ? <Plus size={16} className="text-green-600" /> :
                             tx.type === 'remove' ? <Minus size={16} className="text-red-600" /> :
                             tx.type === 'redeem' ? <Gift size={16} className="text-yellow-600" /> :
                             <Clock size={16} className="text-gray-600" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{tx.description}</p>
                            <p className="text-sm text-gray-500">{formatDate(tx.timestamp)}</p>
                          </div>
                          <div className={`font-semibold ${
                            tx.type === 'add' ? 'text-green-600' : 
                            tx.type === 'remove' ? 'text-red-600' : 
                            'text-yellow-600'
                          }`}>
                            {tx.type === 'add' ? '+' : tx.type === 'remove' ? '-' : ''}{tx.amount}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="font-semibold text-xl text-gray-900 mb-2">Select a Customer</h3>
                <p className="text-gray-500">Choose a customer from the list to manage their rewards</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLoyalty;
